import { useCallback, useEffect, useRef, useState } from "react";
import { Send, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import AISuggestedRepliesPanel from "../components/AISuggestedRepliesPanel";
import ThreadSummaryPanel from "../components/ThreadSummaryPanel";
import TranslateToggle from "../components/TranslateToggle";
import SentimentAlertsBanner from "../components/SentimentAlertsBanner";
import AddAddonDialog from "../components/AddAddonDialog";

const AI_MESSAGING_ADDON = {
  id: "addon_ai_messaging",
  name: "AI Messaging Assistant",
  amount: 49.00,
  description: "AI-drafted replies, thread summaries, auto-translation & sentiment alerts.",
};

export default function MessagesPage() {
  const { user } = useAuth();
  const isFamily = user.role === "family";
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(isFamily ? user.id : null);
  const [activeName, setActiveName] = useState(isFamily ? "Your agency" : "");
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const bottomRef = useRef(null);

  const checkAi = useCallback(() => {
    api.get("/messages/ai/feature-status").then((r) => setAiEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    // Check AI Messaging Assistant access
    checkAi();
  }, [checkAi]);

  const loadThreads = useCallback(() => api.get("/messages/threads").then((r) => setThreads(r.data)), []);
  const loadMessages = useCallback(() => {
    if (!active) return Promise.resolve();
    return api.get(`/messages/${active}`).then((r) => setMessages(r.data));
  }, [active]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    setMessages([]);
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    const t = setInterval(() => {
      loadMessages();
      loadThreads();
    }, 5000);
    return () => clearInterval(t);
  }, [loadMessages, loadThreads]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${active}`, { body });
      setMessages((m) => [...m, data]);
      setBody("");
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout>
      <div data-testid="messages-page" className="space-y-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C07A60]">Messaging</p>
          <h1 className="mt-2 text-3xl font-heading font-light tracking-tight">
            {isFamily ? (
              <>Talk to <span className="font-bold text-[#2E5C50]">your agency</span></>
            ) : (
              <>Family <span className="font-bold text-[#2E5C50]">conversations</span></>
            )}
          </h1>
        </div>

        {!isFamily && <SentimentAlertsBanner enabled={aiEnabled} onLockClick={() => setShowUpgrade(true)} />}

        <div className="grid lg:grid-cols-4 gap-6">
          {!isFamily && (
            <aside data-testid="threads-list" className="lg:col-span-1 bg-white border border-[#E8E6E1] rounded-xl overflow-hidden self-start">
              {threads === null ? (
                <div className="p-5 flex justify-center">
                  <Loader2 className="w-5 h-5 text-[#2E5C50] animate-spin" strokeWidth={1.5} />
                </div>
              ) : threads.length === 0 ? (
                <p className="p-5 text-sm text-[#57605A]">No family accounts yet.</p>
              ) : (
                threads.map((t) => (
                  <button
                    key={t.family_id}
                    data-testid={`thread-item-${t.family_id}`}
                    onClick={() => {
                      setActive(t.family_id);
                      setActiveName(t.family_name);
                    }}
                    className={`w-full text-left px-4 py-3.5 border-b border-[#E8E6E1] last:border-0 transition-colors duration-150 ${
                      active === t.family_id ? "bg-[#D6E0D9]/30" : "hover:bg-[#F9F7F4]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-sm text-[#1C211F] truncate">{t.family_name}</p>
                      {t.unread > 0 && (
                        <span data-testid={`unread-badge-${t.family_id}`} className="bg-[#C07A60] text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                          {t.unread}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-[#57605A] truncate">
                      {t.last_message ? `${t.last_sender}: ${t.last_message}` : "No messages yet"}
                    </p>
                  </button>
                ))
              )}
            </aside>
          )}

          <div className={`${isFamily ? "lg:col-span-4" : "lg:col-span-3"} bg-white border border-[#E8E6E1] rounded-xl flex flex-col h-[600px]`}>
            {!active ? (
              <div className="flex-1 flex flex-col items-center justify-center text-[#57605A]">
                <MessageSquare className="w-8 h-8 text-[#C9CFC9]" strokeWidth={1.5} />
                <p className="mt-3 text-sm">Select a family to start the conversation.</p>
              </div>
            ) : (
              <>
                <div className="border-b border-[#E8E6E1] px-6 py-4 flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-heading font-semibold text-[#1C211F]">{isFamily ? "Your agency thread" : activeName}</p>
                    <p className="text-xs text-[#57605A]">Clear, empathetic, action-oriented — Nexari keeps everyone aligned.</p>
                  </div>
                  {!isFamily && (
                    <div className="flex items-center gap-2">
                      <ThreadSummaryPanel
                        familyId={active}
                        familyName={activeName}
                        enabled={aiEnabled}
                        messageCount={messages.length}
                        onLockClick={() => setShowUpgrade(true)}
                      />
                    </div>
                  )}
                </div>
                <div data-testid="messages-list" className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                  {messages.length === 0 && (
                    <p className="text-center text-sm text-[#57605A] py-10">No messages yet — say hello.</p>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                          mine ? "bg-[#2E5C50] text-white rounded-br-sm" : "bg-[#F9F7F4] border border-[#E8E6E1] text-[#1C211F] rounded-bl-sm"
                        }`}>
                          {!mine && (
                            <p className="text-xs font-semibold text-[#C07A60] mb-0.5">
                              {m.sender_name} · {m.sender_role}
                            </p>
                          )}
                          <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.body}</p>
                          <p className={`mt-1 text-[10px] ${mine ? "text-white/60" : "text-[#57605A]"}`}>
                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="border-t border-[#E8E6E1] p-4 space-y-2">
                  {!isFamily && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <AISuggestedRepliesPanel
                        familyId={active}
                        enabled={aiEnabled}
                        onPickReply={(text) => setBody(text)}
                        onLockClick={() => setShowUpgrade(true)}
                      />
                      <TranslateToggle
                        enabled={aiEnabled}
                        currentText={body}
                        onTranslated={(t) => setBody(t)}
                        onLockClick={() => setShowUpgrade(true)}
                      />
                    </div>
                  )}
                  <div className="flex gap-3">
                    <input
                      data-testid="message-input"
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Write a message…"
                      className="flex-1 bg-[#F9F7F4] border border-[#E8E6E1] rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
                    />
                    <button
                      data-testid="send-message-btn"
                      disabled={sending || !body.trim()}
                      className="flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-5 py-2.5 text-sm font-medium disabled:opacity-50"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                      Send
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
        <AddAddonDialog
          addon={AI_MESSAGING_ADDON}
          open={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          onAdded={() => { checkAi(); setShowUpgrade(false); }}
        />
      </div>
    </Layout>
  );
}
